import { useState, FormEvent, useMemo } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { resources } from "@constants/hardcoded-strings";
import { ButtonTypes } from "@enums/button-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ModalSizeTypes } from "@enums/modal-size-types";
import { SelectOption } from "@idw-types/common";
import { FormattedRpServiceConfigurationIdentityProvider } from "@idw-types/data/rp-service-configuration";
import { sortStrings } from "@utils/string-util";
import { DefaultModal } from "../../common/modals/default-modal/default-modal";
import { SelectFormGroup } from "../../common/select-form-group/select-form-group";

type AddRpServiceConfigurationIdentityProviderModalProps = {
    onModalClose: () => void;
    rpServiceConfigurationIdentityProviders: Array<FormattedRpServiceConfigurationIdentityProvider>;
    addRpServiceConfigurationIdentityProvider: (rpServiceConfigurationIdentityProvider: FormattedRpServiceConfigurationIdentityProvider) => void;
};

type AddRpServiceConfigurationIdentityProviderModalState = {
    identityProvider: FormattedRpServiceConfigurationIdentityProvider;
    selectedIdentityProviderOption: SelectOption;
};

export const AddRpServiceConfigurationIdentityProviderModal = (props: AddRpServiceConfigurationIdentityProviderModalProps) => {
    const { onModalClose, rpServiceConfigurationIdentityProviders, addRpServiceConfigurationIdentityProvider } = props;

    const [labelWidth] = useState(LabelWidthTypes.SMALL);
    const [state, setState] = useState<AddRpServiceConfigurationIdentityProviderModalState>({
        identityProvider: null,
        selectedIdentityProviderOption: null,
    });

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        addRpServiceConfigurationIdentityProvider({
            ...state.identityProvider,
            addedToService: true
        });
        onModalClose();
    };

    const handleIdentityProviderChange = (newValue: SelectOption) => {
        const rpServiceConfigurationIdentityProvider = rpServiceConfigurationIdentityProviders.find(idp => idp.identityProvider.id === newValue.value);
        setState({
            identityProvider: rpServiceConfigurationIdentityProvider,
            selectedIdentityProviderOption: newValue,
        });
    };

    const identityProviderOptions = useMemo(() => {
        return rpServiceConfigurationIdentityProviders
            .map(idp => ({ label: idp.identityProvider.name, value: idp.identityProvider.id }))
            .sort((a, b) => sortStrings(a.label, b.label));
    }, [rpServiceConfigurationIdentityProviders]);

    return (
        <DefaultModal
            automationId="add-identity-provider-modal"
            ariaLabelledBy="add-identity-provider-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
            size={ModalSizeTypes.MEDIUM}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header id={"add-identity-provider-title"} closeButton>
                    <Modal.Title>{resources.modals.addRpServiceConfigurationIdentityProvider.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <SelectFormGroup
                        controlId={"identity-provider-select"}
                        automationId={"identity-provider-select"}
                        ariaLabel={resources.modals.addRpServiceConfigurationIdentityProvider.inputs.identityProvider.label}
                        label={resources.modals.addRpServiceConfigurationIdentityProvider.inputs.identityProvider.label}
                        disabled={false}
                        isClearable={false}
                        isRequired={true}
                        isSearchable={true}
                        labelWidth={labelWidth}
                        onChange={handleIdentityProviderChange}
                        options={identityProviderOptions}
                        selectedOption={state.selectedIdentityProviderOption}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                    <Button
                        disabled={state.selectedIdentityProviderOption === null}
                        variant="primary"
                        type={ButtonTypes.SUBMIT}
                    >
                        {resources.modals.addRpServiceConfigurationIdentityProvider.buttons.addIdentityProvider.label}
                    </Button>
                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
};